import type { ConversationView, MessageView } from '@hilo/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import { Fragment, useEffect, useLayoutEffect, useRef } from 'react';
import { isUnavailable } from '../../lib/api';
import { KIND_LABEL, messageErrorText, REFERRAL_LABEL, statusDetail, statusLabel } from '../../lib/copy';
import { setDraftText } from '../../lib/drafts';
import { useCurrentUser } from '../../lib/session';
import { StatusTicks } from '../../ui/StatusTicks';
import { AttachmentView } from './AttachmentView';
import { formatDayHeading, formatTime, sameDay } from '../../lib/time';
import styles from './ConversationThread.module.css';
import { draftEvents } from './draftEvents';

/**
 * El hilo como costura: los mensajes del cliente a un lado, los de la empresa al otro, cosidos a un hilo
 * añil central. El punto sobre el hilo de cada respuesta marca su estado; el texto lo repite siempre.
 */
export function ConversationThread({ conversation, messages, canReuse }: {
  conversation: ConversationView;
  messages: UseQueryResult<MessageView[]>;
  canReuse: boolean;
}) {
  const user = useCurrentUser();
  const scroller = useRef<HTMLDivElement>(null);
  const nearBottom = useRef(true);
  const items = messages.data ?? [];
  const lastId = items.at(-1)?.id;

  // Al abrir y cuando llega algo nuevo, bajar al final si la persona ya estaba abajo.
  useLayoutEffect(() => {
    const element = scroller.current;
    if (element && nearBottom.current) element.scrollTop = element.scrollHeight;
  }, [lastId]);
  useEffect(() => {
    nearBottom.current = true;
  }, [conversation.id]);

  // Las imágenes cambian la altura al cargar: si la persona estaba abajo, se queda abajo.
  useEffect(() => {
    const element = scroller.current;
    const content = element?.firstElementChild?.nextElementSibling ?? element?.firstElementChild;
    if (!element || !content || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (nearBottom.current) element.scrollTop = element.scrollHeight;
    });
    for (const child of element.children) observer.observe(child);
    return () => observer.disconnect();
  }, [items.length]);

  const onScroll = () => {
    const element = scroller.current;
    if (element) nearBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
  };

  const reuse = (message: MessageView) => {
    if (!message.text) return;
    setDraftText(conversation.id, message.text);
    draftEvents.dispatchEvent(new CustomEvent('reused', { detail: conversation.id }));
  };

  return (
    <div className={styles.scroller} ref={scroller} onScroll={onScroll}>
      {messages.isError && isUnavailable(messages.error) && (
        <p className={styles.notice} role="status">Hilo no responde: el hilo puede estar desactualizado. Se reintenta solo.</p>
      )}
      {messages.isPending && <p className={styles.notice}>Cargando mensajes…</p>}
      {messages.isSuccess && items.length === 0 && <p className={styles.notice}>Todavía no hay mensajes en esta conversación.</p>}
      {items.length > 0 && <ol className={styles.seam} aria-label="Mensajes" role="log">
        {items.map((message, index) => {
          const previous = items[index - 1];
          const newDay = !previous || !sameDay(previous.receivedAt, message.receivedAt, user.timezone);
          return (
            <Fragment key={message.id}>
              {newDay && (
                <li className={styles.day} aria-hidden="true">
                  <span>{formatDayHeading(message.receivedAt, user.timezone)}</span>
                </li>
              )}
              <MessageRow message={message} conversation={conversation} timeZone={user.timezone}
                onReuse={canReuse && message.text ? () => reuse(message) : null} />
            </Fragment>
          );
        })}
      </ol>}
    </div>
  );
}

function MessageRow({ message, conversation, timeZone, onReuse }: {
  message: MessageView; conversation: ConversationView; timeZone: string; onReuse: (() => void) | null;
}) {
  const outbound = message.direction === 'OUTBOUND';
  const time = formatTime(message.occurredAt, timeZone);
  const status = outbound ? statusLabel(message.status, conversation.channel) : null;
  const detail = outbound ? statusDetail(message.status, conversation.channel) : null;
  const trouble = outbound && (message.status === 'FAILED' || message.status === 'UNKNOWN');
  const who = outbound ? (message.authorDisplayName ?? 'Hilo') : null;
  const hasMedia = message.attachmentIds.length > 0 || (message.kind !== 'TEXT' && message.kind !== 'UNSUPPORTED');

  return (
    <li className={styles.row} data-direction={outbound ? 'out' : 'in'}>
      <span className={styles.stitch} data-status={outbound ? message.status : 'INBOUND'} aria-hidden="true" />
      <article className={styles.bubble} data-trouble={trouble || undefined}
        aria-label={outbound ? `Enviado por ${who}, ${time}, ${status}` : `Mensaje del cliente, ${time}`}>
        {message.referral && (
          <p className={styles.referral}>
            Llegó desde {REFERRAL_LABEL[message.referral.kind].toLowerCase()}
            {message.referral.title ? <> «{message.referral.title}»</> : null}
          </p>
        )}
        {message.attachments.length > 0 && (
          <div className={styles.attachments}>
            {message.attachments.map((attachment) => <AttachmentView key={attachment.id} attachment={attachment} />)}
          </div>
        )}
        {hasMedia && message.attachments.length === 0 && (
          <p className={styles.media}>
            {message.kind === 'TEXT' ? 'Adjunto' : KIND_LABEL[message.kind]} · el archivo todavía no está disponible
          </p>
        )}
        {message.text && <p className={styles.text}>{message.text}</p>}
        {message.kind === 'UNSUPPORTED' && (
          <p className={styles.media}>El cliente envió un contenido que Hilo todavía no puede mostrar.</p>
        )}
        <footer className={styles.meta}>
          {who && <span className={styles.who}>{who}</span>}
          <time dateTime={message.occurredAt}>{time}</time>
          {status && (
            <span className={styles.status} data-status={message.status}>
              <StatusTicks status={message.status} />
              {status}
            </span>
          )}
        </footer>
        {(detail || trouble) && (
          <div className={styles.trouble}>
            {detail && <p>{detail}</p>}
            {message.status === 'FAILED' && messageErrorText(message.errorCode, conversation.channel) && <p>{messageErrorText(message.errorCode, conversation.channel)}</p>}
            {message.status === 'UNKNOWN' && onReuse && <p>Si lo envías de nuevo, el cliente podría recibirlo dos veces.</p>}
            {trouble && onReuse && (
              <button type="button" className="btn btn-quiet" onClick={onReuse}>Copiar al editor</button>
            )}
          </div>
        )}
      </article>
    </li>
  );
}
